import { Api } from '@/api/api.client';
import { Components } from '@/api/generated/api';
import { useConfirmDialog } from '@/composables/useConfirmDialog';
import { useDisplayName } from '@/composables/useDisplayName';
import { usePermissions } from '@/composables/usePermissions';
import { useQueryWithToast } from '@/composables/useQueryWithToast';
import { useToast } from '@/composables/useToast';
import { useCurrentClass } from '@/schoolClasses/useCurrentClass';
import { useWhoAmIStore } from '@/stores/useWhoAmIStore';
import { performanceVisualKeys } from '@/composables/performanceVisual.keys';
import { invalidateSubjectSpecificPerformanceAssessmentQueries } from '@/composables/performanceAssessment/subjectSpecificPerformanceAssessmentQueryInvalidation';
import { buildPerformanceAssessmentDeleteConfirmation, getPerformanceAssessmentName } from '@/utils/performance.utils';
import { FilterOperationType, IFilter } from '@chax-at/prisma-filter-common';
import { useQueryClient } from '@tanstack/vue-query';
import { IPerformanceAssessmentFilter, IPerformanceAssessmentTemplateFilter } from 'common/interface/filter.interface';
import { computed, type ComputedRef } from 'vue';
import { useI18n } from 'vue-i18n';
import { invalidatePerformanceAssessmentEntryQueries } from './performanceAssessmentEntry/performanceAssessmentEntryQueryInvalidation';
import { performanceAssessmentKeys } from './performanceAssessment.keys';

interface IImportPerformanceAssessmentTemplateParams {
  performanceAssessmentId: number;
  schoolClassId?: number;
  schoolSubjectId: number | null;
}

interface IUpdatePerformanceAssessmentOptions {
  showToast?: boolean;
}

interface IPerformanceAssessmentComposable {
  allPerformanceAssessmentsAdmin: ComputedRef<Components.Schemas.PerformanceAssessmentDto[]>;
  allPerformanceAssessmentsTeacher: ComputedRef<Components.Schemas.PerformanceAssessmentDto[]>;
  isLoading: ComputedRef<boolean>;
  createPerformanceAssessment: (
    dto: Components.Schemas.CreatePerformanceAssessmentDto,
  ) => Promise<Components.Schemas.PerformanceAssessmentDto>;
  deletePerformanceAssessment: (performanceAssessment: Components.Schemas.PerformanceAssessmentDto) => Promise<void>;
  getAvailablePerformanceAssessments: (schoolLevel: number) => Promise<Components.Schemas.PerformanceAssessmentDto[]>;
  importPerformanceAssessmentTemplate: (params: IImportPerformanceAssessmentTemplateParams) => Promise<void>;
  getAvailablePerformanceAssessmentsTemplates: (
    schoolLevel: number,
  ) => Promise<Components.Schemas.PerformanceAssessmentDto[]>;
  updatePerformanceAssessment: (
    performanceAssessmentId: number,
    dto: Components.Schemas.UpdatePerformanceAssessmentDto,
    options?: IUpdatePerformanceAssessmentOptions,
  ) => Promise<void>;
}

export const usePerformanceAssessment = (): IPerformanceAssessmentComposable => {
  const { hasTenantAdminAccess } = usePermissions();
  const { createSuccessToast, deleteSuccessToast, editSuccessToast, catchError } = useToast();
  const { currentClass } = useCurrentClass();
  const { confirm } = useConfirmDialog();
  const { getUserDisplayName } = useDisplayName();
  const whoAmIStore = useWhoAmIStore();
  const { t } = useI18n();

  // Admin
  const getPerformanceAssessmentsAdmin = async (): Promise<Components.Schemas.PerformanceAssessmentDto[]> => {
    if (!hasTenantAdminAccess.value) return [];
    const filter: IFilter<IPerformanceAssessmentFilter> = {
      filter: [{ field: 'isTemplate', type: FilterOperationType.Eq, value: true }],
      order: [
        { field: 'order', dir: 'asc' },
        { field: 'id', dir: 'asc' },
      ],
    };

    const result = await Api.Routes.PerformanceAssessment.retrieve({
      $filter: filter,
    });
    return result.rows;
  };

  const performanceAssessmentAdminListQueryKey = computed(() =>
    performanceAssessmentKeys.adminList(hasTenantAdminAccess.value),
  );
  const queryClientAdmin = useQueryClient();
  const adminQuery = useQueryWithToast({
    queryKey: performanceAssessmentAdminListQueryKey,
    queryFn: getPerformanceAssessmentsAdmin,
  });

  const allPerformanceAssessmentsAdmin = computed<Components.Schemas.PerformanceAssessmentDto[]>(() => {
    return adminQuery.data.value ?? [];
  });

  // Teacher
  const getPerformanceAssessmentsTeacher = async (): Promise<Components.Schemas.PerformanceAssessmentDto[]> => {
    if (!currentClass.value) return [];
    const filter: IFilter<IPerformanceAssessmentFilter> = {
      filter: [
        { field: 'isTemplate', type: FilterOperationType.Eq, value: false },
        { field: 'schoolClassId', type: FilterOperationType.Eq, value: currentClass.value.id },
      ],
      order: [
        { field: 'order', dir: 'asc' },
        { field: 'id', dir: 'asc' },
      ],
    };

    const result = await Api.Routes.PerformanceAssessment.retrieve({
      $filter: filter,
    });
    return result.rows;
  };

  const performanceAssessmentTeacherListQueryKey = computed(() =>
    performanceAssessmentKeys.teacherList(currentClass.value?.id),
  );
  const queryClientTeacher = useQueryClient();
  const teacherQuery = useQueryWithToast({
    queryKey: performanceAssessmentTeacherListQueryKey,
    queryFn: getPerformanceAssessmentsTeacher,
  });

  const allPerformanceAssessmentsTeacher = computed<Components.Schemas.PerformanceAssessmentDto[]>(() => {
    return teacherQuery.data.value ?? [];
  });

  const isLoading = computed<boolean>(() => adminQuery.isLoading.value || teacherQuery.isLoading.value);

  const refresh = async (): Promise<void> => {
    await Promise.all([
      queryClientAdmin.invalidateQueries({ queryKey: performanceAssessmentAdminListQueryKey }),
      queryClientTeacher.invalidateQueries({ queryKey: performanceAssessmentTeacherListQueryKey }),
      queryClientTeacher.invalidateQueries({ queryKey: performanceVisualKeys.lists() }),
      invalidatePerformanceAssessmentEntryQueries(queryClientTeacher, currentClass.value?.id),
    ]);
  };

  const refreshAfterAssessmentAdded = async (schoolSubjectId: number | null, schoolClassId?: number): Promise<void> => {
    if (schoolSubjectId == null) {
      await refresh();
      return;
    }

    await Promise.all([
      invalidateSubjectSpecificPerformanceAssessmentQueries(queryClientTeacher, schoolClassId),
      queryClientTeacher.invalidateQueries({ queryKey: performanceVisualKeys.lists() }),
    ]);
  };

  const createPerformanceAssessment = async (
    dto: Components.Schemas.CreatePerformanceAssessmentDto,
  ): Promise<Components.Schemas.PerformanceAssessmentDto> => {
    return catchError(async () => {
      const res = await Api.Routes.PerformanceAssessment.create(null, dto);
      await refreshAfterAssessmentAdded(dto.schoolSubjectId, dto.schoolClassId);
      createSuccessToast();
      return res.data;
    });
  };

  const deletePerformanceAssessment = async (
    performanceAssessment: Components.Schemas.PerformanceAssessmentDto,
  ): Promise<void> => {
    const confirmed = await confirm(
      buildPerformanceAssessmentDeleteConfirmation({
        assessmentName: getPerformanceAssessmentName(performanceAssessment, t),
        confirmationValue: whoAmIStore.whoAmI ? getUserDisplayName(whoAmIStore.whoAmI) : '',
        entryCount: performanceAssessment.entryCount,
        t,
      }),
    );

    if (!confirmed) return;

    await catchError(async () => {
      await Api.Routes.PerformanceAssessment.delete({ id: performanceAssessment.id });
      await refresh();
      deleteSuccessToast();
    });
  };

  const updatePerformanceAssessment = async (
    performanceAssessmentId: number,
    dto: Components.Schemas.UpdatePerformanceAssessmentDto,
    options?: IUpdatePerformanceAssessmentOptions,
  ): Promise<void> => {
    await catchError(async () => {
      await Api.Routes.PerformanceAssessment.update({ id: performanceAssessmentId }, dto);
      await refresh();
      if (options?.showToast !== false) {
        editSuccessToast();
      }
    });
  };

  const getAvailablePerformanceAssessments = async (
    schoolLevel: number,
  ): Promise<Components.Schemas.PerformanceAssessmentDto[]> => {
    const availablePerformanceAssessmentsFilter: IFilter<IPerformanceAssessmentFilter> = {
      filter: [
        { field: 'isTemplate', type: FilterOperationType.Eq, value: true },
        { field: 'schoolLevel', type: FilterOperationType.Eq, value: schoolLevel },
      ],
      order: [
        { field: 'order', dir: 'asc' },
        { field: 'id', dir: 'asc' },
      ],
    };
    const result = await Api.Routes.PerformanceAssessment.retrieve({ $filter: availablePerformanceAssessmentsFilter });
    return result.rows;
  };

  const getAvailablePerformanceAssessmentsTemplates = async (
    schoolLevel: number,
  ): Promise<Components.Schemas.PerformanceAssessmentDto[]> => {
    const availablePerformanceAssessmentsFilter: IFilter<IPerformanceAssessmentTemplateFilter> = {
      filter: [{ field: 'schoolLevel', type: FilterOperationType.Eq, value: schoolLevel }],
      order: [
        { field: 'order', dir: 'asc' },
        { field: 'id', dir: 'asc' },
      ],
    };
    const result = await Api.Routes.Tenant.retrieveTenantPerformanceAssessmentTemplates({
      $filter: availablePerformanceAssessmentsFilter,
    });
    return result.rows;
  };

  const importPerformanceAssessmentTemplate = async (
    params: IImportPerformanceAssessmentTemplateParams,
  ): Promise<void> => {
    await catchError(async () => {
      if (params.schoolClassId) {
        await Api.Routes.SchoolClass.importTenantTemplateToClass(
          {
            performanceAssessmentId: params.performanceAssessmentId,
            schoolClassId: params.schoolClassId,
          },
          { schoolSubjectId: params.schoolSubjectId },
        );
      } else {
        await Api.Routes.Tenant.importProviderTemplateToTenant(
          {
            performanceAssessmentId: params.performanceAssessmentId,
          },
          { schoolSubjectId: params.schoolSubjectId },
        );
      }
      await refreshAfterAssessmentAdded(params.schoolSubjectId, params.schoolClassId);
      createSuccessToast();
    });
  };

  return {
    allPerformanceAssessmentsAdmin,
    allPerformanceAssessmentsTeacher,
    isLoading,
    createPerformanceAssessment,
    deletePerformanceAssessment,
    getAvailablePerformanceAssessments,
    getAvailablePerformanceAssessmentsTemplates,
    importPerformanceAssessmentTemplate,
    updatePerformanceAssessment,
  };
};
